import React from 'react';
import type { BundleCreateResponse } from '../../../services/adOpsApi';
import { Button, Card, StatusPill } from '../../ui';
import KeywordChip from './KeywordChip';

interface BundleSuccessCardProps {
  bundle: BundleCreateResponse;
  copyState: 'idle' | 'copied' | 'failed';
  onCopyContentIds: () => void;
  onReset: () => void;
}

/** §4.6 success banner. */
const successBanner: React.CSSProperties = {
  background: 'var(--success-soft)',
  border: '1px solid var(--cg-forest-200)',
  color: 'var(--success)',
  borderRadius: 'var(--radius-md)',
  padding: '10px 14px',
  fontSize: 13,
  fontWeight: 500,
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
};

const title: React.CSSProperties = {
  margin: 0,
  fontSize: 15,
  fontWeight: 700,
  color: 'var(--success)',
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 8,
};

const subtitle: React.CSSProperties = {
  margin: '4px 0 0',
  fontSize: 13,
  fontWeight: 400,
  color: 'var(--fg-2)',
  lineHeight: 1.5,
};

const actions: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  alignItems: 'center',
};

/** Anchor styled to match ui/Button `primary` md (links can't render as <Button>). */
const primaryLink: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 36,
  padding: '0 14px',
  borderRadius: 'var(--radius-md)',
  background: 'var(--accent)',
  border: '1px solid var(--accent)',
  color: 'var(--fg-on-accent)',
  fontSize: 13.5,
  fontWeight: 600,
  textDecoration: 'none',
  fontFamily: 'var(--font-sans)',
  boxShadow: 'var(--shadow-sm)',
  whiteSpace: 'nowrap',
};

const linkBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--accent)',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  padding: 0,
  fontFamily: 'var(--font-sans)',
};

const section: React.CSSProperties = {
  fontSize: 12,
};

/** Micro-label lock (§2.2). */
const sectionLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 'var(--track-eyebrow)',
  textTransform: 'uppercase',
  color: 'var(--fg-4)',
  marginBottom: 6,
};

const chipWrap: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
};

const dropSummary: React.CSSProperties = {
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--fg-2)',
};

const idTextarea: React.CSSProperties = {
  width: '100%',
  minHeight: 120,
  marginTop: 6,
  padding: 8,
  fontFamily: 'var(--font-mono)',
  fontSize: 12,
  border: '1px solid var(--border-2)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--cg-neutral-0)',
  color: 'var(--fg-1)',
  boxSizing: 'border-box',
  resize: 'vertical',
};

const footer: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--fg-3)',
  lineHeight: 1.5,
  fontStyle: 'italic',
};

const copyButtonLabel = (
  state: 'idle' | 'copied' | 'failed',
  count: number,
): string => {
  if (state === 'copied') return '✓ Copied';
  if (state === 'failed') return 'Copy failed — select manually below';
  return `Copy ${count} content IDs`;
};

const BundleSuccessCard: React.FC<BundleSuccessCardProps> = ({
  bundle,
  copyState,
  onCopyContentIds,
  onReset,
}) => {
  const ids = bundle.suggestedContentIds;
  return (
    <Card style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={successBanner}>
        <div style={{ fontSize: 22, lineHeight: 1 }}>✅</div>
        <div>
          <h2 style={title}>
            <span>Bundle &ldquo;{bundle.bundle.name}&rdquo;</span>
            <StatusPill variant="ok" noDot>created</StatusPill>
            {bundle.dryRun && <StatusPill variant="draft" noDot>dry-run</StatusPill>}
          </h2>
          <p style={subtitle}>
            {bundle.dryRun
              ? 'Dry-run — no GAM write was performed. The handoff steps below preview how the real flow will look.'
              : 'One more step in GAM: open the bundle, paste the content IDs into its Content tab, and click Save.'}
          </p>
        </div>
      </div>

      <div style={actions}>
        {bundle.gamUrl ? (
          <a href={bundle.gamUrl} target="_blank" rel="noreferrer" style={primaryLink}>
            Open in GAM →
          </a>
        ) : (
          <Button variant="primary" size="md" disabled title="Dry-run — no real bundle to open">
            Open in GAM →
          </Button>
        )}
        <Button variant="ghost" size="md" onClick={onCopyContentIds}>
          {copyButtonLabel(copyState, ids.length)}
        </Button>
        <button onClick={onReset} style={linkBtn}>
          Start over
        </button>
      </div>

      {bundle.matchedKeywords.length > 0 && (
        <div style={section}>
          <div style={sectionLabel}>Matched keywords</div>
          <div style={chipWrap}>
            {bundle.matchedKeywords.map((k) => (
              <KeywordChip key={k} keyword={k} />
            ))}
          </div>
        </div>
      )}

      <details style={section}>
        <summary style={dropSummary}>
          Show {ids.length} content IDs (paste target)
        </summary>
        <textarea
          readOnly
          value={ids.join('\n')}
          style={idTextarea}
          onFocus={(e) => e.currentTarget.select()}
        />
      </details>

      <div style={footer}>
        v202605 ContentBundleService doesn&apos;t expose membership over SOAP, so the bundle was
        created empty. Pasting in the GAM UI is the supported way to populate it; the suggested
        list above is mirrored in the audit log under <code>suggestedContentIds</code>.
      </div>
    </Card>
  );
};

export default BundleSuccessCard;
